import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSettingsStore } from '@/stores/settings.store'
import { useGroupsStore } from '@/stores/groups.store'
import { useAuth } from '@/hooks/useAuth'
import { canEditSettings, canManageApk } from '@/utils/role-access'
import { apkService } from '@/services/apk.service'
import { ApkInfo } from '@/types'
import { DefaultApkSection } from '@/components/apk/DefaultApkSection'
import { EquipmentConfig, ServerConfig, Certificates } from '@/types'
import { EquipmentConfigSection } from '@/components/settings/EquipmentConfigSection'
import { ServerConfigSection } from '@/components/settings/ServerConfigSection'
import { CertificatesSection } from '@/components/settings/CertificatesSection'
import { RegimeFileSection } from '@/components/settings/RegimeFileSection'
import { BillingConfigSection } from '@/components/settings/BillingConfigSection'

type SettingsTab = 'equipment' | 'server' | 'certificates' | 'regime' | 'apk' | 'billing'

const GROUP_SCOPED_TABS: SettingsTab[] = ['equipment', 'server', 'certificates']

export const SettingsPage: React.FC = () => {
  const settings = useSettingsStore(state => state.settings)
  const groupSettings = useSettingsStore(state => state.groupSettings)
  const selectedGroupId = useSettingsStore(state => state.selectedGroupId)
  const isLoading = useSettingsStore(state => state.isLoading)
  const isGroupSettingsLoading = useSettingsStore(state => state.isGroupSettingsLoading)
  const fetchSettings = useSettingsStore(state => state.fetchSettings)
  const fetchGroupSettings = useSettingsStore(state => state.fetchGroupSettings)
  const setSelectedGroupId = useSettingsStore(state => state.setSelectedGroupId)
  const updateEquipmentConfig = useSettingsStore(state => state.updateEquipmentConfig)
  const updateServerConfig = useSettingsStore(state => state.updateServerConfig)
  const updateCertificates = useSettingsStore(state => state.updateCertificates)
  const uploadRegime = useSettingsStore(state => state.uploadRegime)
  const isUploadingRegime = useSettingsStore(state => state.isLoading)
  const groups = useGroupsStore(state => state.groups)
  const fetchGroups = useGroupsStore(state => state.fetchGroups)
  const { user } = useAuth()
  const canEdit = canEditSettings(user)
  const canEditApk = canManageApk(user)

  const [activeTab, setActiveTab] = useState<SettingsTab>('equipment')
  const [defaultApk, setDefaultApk] = useState<ApkInfo | null | undefined>(undefined)
  const [isApkUploading, setIsApkUploading] = useState(false)
  const hasFetchedRef = useRef(false)
  const hasFetchedApkRef = useRef(false)
  const hasFetchedGroupsRef = useRef(false)

  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true

    fetchSettings().catch(error => {
      console.error('Error loading settings:', error)
    })
  }, [fetchSettings])

  useEffect(() => {
    if (hasFetchedGroupsRef.current) return
    hasFetchedGroupsRef.current = true

    fetchGroups().catch(error => {
      console.error('Error loading groups:', error)
    })
  }, [fetchGroups])

  useEffect(() => {
    if (!groups?.length) return

    if (!selectedGroupId || !groups.some(group => group._id === selectedGroupId)) {
      setSelectedGroupId(groups[0]._id)
    }
  }, [groups, selectedGroupId, setSelectedGroupId])

  useEffect(() => {
    if (!selectedGroupId || !GROUP_SCOPED_TABS.includes(activeTab)) return

    fetchGroupSettings(selectedGroupId).catch(error => {
      console.error('Error loading group settings:', error)
    })
  }, [selectedGroupId, activeTab, fetchGroupSettings])

  const handleEquipmentSave = useCallback(
    async (config: EquipmentConfig) => {
      if (!selectedGroupId) return
      await updateEquipmentConfig(selectedGroupId, config)
    },
    [selectedGroupId, updateEquipmentConfig]
  )

  const handleServerSave = useCallback(
    async (config: ServerConfig) => {
      if (!selectedGroupId) return
      await updateServerConfig(selectedGroupId, config)
    },
    [selectedGroupId, updateServerConfig]
  )

  const handleCertificatesSave = useCallback(
    async (certificates: Certificates) => {
      if (!selectedGroupId) return
      await updateCertificates(selectedGroupId, certificates)
    },
    [selectedGroupId, updateCertificates]
  )

  const handleRegimeUpload = useCallback(
    async (file: File) => {
      await uploadRegime(file)
    },
    [uploadRegime]
  )

  const handleRegimeDownload = useCallback(async () => {
    const downloadRegime = useSettingsStore.getState().downloadRegime
    await downloadRegime()
  }, [])

  useEffect(() => {
    if (activeTab !== 'apk' || hasFetchedApkRef.current) return
    hasFetchedApkRef.current = true

    apkService
      .getDefault()
      .then(info => setDefaultApk(info))
      .catch(error => {
        console.error('Error loading default APK:', error)
        setDefaultApk(null)
      })
  }, [activeTab])

  const handleDefaultApkUpload = useCallback(async (file: File) => {
    setIsApkUploading(true)
    try {
      const info = await apkService.uploadDefault(file)
      setDefaultApk(info)
    } finally {
      setIsApkUploading(false)
    }
  }, [])

  const isGroupTab = GROUP_SCOPED_TABS.includes(activeTab)
  const showGroupSelector = isGroupTab

  if (isLoading && !settings) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[var(--color-text-secondary)]">Chargement...</div>
      </div>
    )
  }

  return (
    <div>
      <div className="border-b border-[var(--color-border)] mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('equipment')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'equipment'
                ? 'border-[var(--color-primary)] text-[var(--color-primary)] font-semibold'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border)]'
            }`}
          >
            Configuration de l'équipement
          </button>
          <button
            onClick={() => setActiveTab('server')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'server'
                ? 'border-[var(--color-primary)] text-[var(--color-primary)] font-semibold'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border)]'
            }`}
          >
            Configuration du serveur
          </button>
          <button
            onClick={() => setActiveTab('certificates')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'certificates'
                ? 'border-[var(--color-primary)] text-[var(--color-primary)] font-semibold'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border)]'
            }`}
          >
            Certificats
          </button>
          <button
            onClick={() => setActiveTab('regime')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'regime'
                ? 'border-[var(--color-primary)] text-[var(--color-primary)] font-semibold'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border)]'
            }`}
          >
            Fichier régime
          </button>
          <button
            onClick={() => setActiveTab('apk')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'apk'
                ? 'border-[var(--color-primary)] text-[var(--color-primary)] font-semibold'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border)]'
            }`}
          >
            APK
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'billing'
                ? 'border-[var(--color-primary)] text-[var(--color-primary)] font-semibold'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border)]'
            }`}
          >
            Facturation
          </button>
        </nav>
      </div>

      {showGroupSelector && (
        <div className="mb-6">
          <label
            htmlFor="settings-group"
            className="block text-sm font-medium text-[var(--color-text-primary)] mb-2"
          >
            Groupe
          </label>
          {groups?.length ? (
            <select
              id="settings-group"
              value={selectedGroupId || ''}
              onChange={event => setSelectedGroupId(event.target.value)}
              className="w-full max-w-md rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              {groups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">
              Aucun groupe disponible. Créez un groupe pour configurer ces paramètres.
            </p>
          )}
        </div>
      )}

      {isGroupTab && isGroupSettingsLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-[var(--color-text-secondary)]">Chargement...</div>
        </div>
      )}

      {activeTab === 'equipment' && !isGroupSettingsLoading && selectedGroupId && (
        <EquipmentConfigSection
          key={selectedGroupId}
          initialConfig={groupSettings?.equipmentConfig ?? undefined}
          onSave={handleEquipmentSave}
          readOnly={!canEdit}
        />
      )}

      {activeTab === 'server' && !isGroupSettingsLoading && selectedGroupId && (
        <ServerConfigSection
          key={selectedGroupId}
          initialConfig={groupSettings?.serverConfig ?? undefined}
          onSave={handleServerSave}
          readOnly={!canEdit}
        />
      )}

      {activeTab === 'certificates' && !isGroupSettingsLoading && selectedGroupId && (
        <CertificatesSection
          key={selectedGroupId}
          initialCertificates={groupSettings?.certificates ?? undefined}
          onSave={handleCertificatesSave}
          readOnly={!canEdit}
        />
      )}

      {activeTab === 'regime' && (
        <RegimeFileSection
          currentRegime={settings?.regime}
          onUpload={handleRegimeUpload}
          onDownload={handleRegimeDownload}
          isUploading={isUploadingRegime}
          readOnly={!canEdit}
        />
      )}

      {activeTab === 'apk' &&
        (defaultApk === undefined ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-[var(--color-text-secondary)]">Chargement...</div>
          </div>
        ) : (
          <DefaultApkSection
            currentApk={defaultApk}
            onUpload={handleDefaultApkUpload}
            isUploading={isApkUploading}
            readOnly={!canEditApk}
          />
        ))}

      {activeTab === 'billing' && <BillingConfigSection readOnly={!canEdit} />}
    </div>
  )
}
